import React, { useCallback, useEffect, useState } from 'react';
import { HiPencil, HiTrash, HiLockClosed } from 'react-icons/hi';
import { documentTypesService } from '../services/api';
import { useDocumentTypes } from '../context/DocumentTypesContext';
import { apiError } from '../utils/apiError';

const EMPTY = { label: '', description: '', sort_order: 100, is_active: true };

const FormModal = ({ type, onSubmit, onCancel, saving }) => {
  const isEdit = Boolean(type);
  const [form, setForm] = useState(() =>
    type
      ? {
          label: type.label ?? '',
          description: type.description ?? '',
          sort_order: type.sort_order ?? 100,
          is_active: type.is_active ?? true,
        }
      : EMPTY
  );
  const [error, setError] = useState(null);
  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.label.trim()) return setError('Label is required');
    setError(null);
    onSubmit({
      ...form,
      label: form.label.trim(),
      description: form.description.trim() || null,
      sort_order: Number(form.sort_order) || 100,
    });
  };

  const input =
    'w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-1">
          {isEdit ? 'Edit document type' : 'Add document type'}
        </h3>
        <p className="text-sm text-slate-500 mb-5">
          Available immediately in every picker and filter — no restart needed.
        </p>

        {error && <div className="mb-4 px-4 py-3 rounded-lg text-sm bg-red-50 text-red-700">{error}</div>}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Label</label>
            <input
              type="text"
              value={form.label}
              onChange={(e) => setField('label', e.target.value)}
              placeholder="e.g. HR ADM Receipt"
              className={input}
            />
            {!isEdit && (
              <p className="text-xs text-slate-400 mt-1">
                A stable key is derived from the label and cannot change later.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Description <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              className={input}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Sort order</label>
              <input
                type="number"
                min={0}
                max={9999}
                value={form.sort_order}
                onChange={(e) => setField('sort_order', e.target.value)}
                className={input}
              />
              <p className="text-xs text-slate-400 mt-1">Lower numbers appear first.</p>
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setField('is_active', e.target.checked)}
                  className="w-4 h-4 accent-blue-600"
                />
                Active
              </label>
            </div>
          </div>

          {isEdit && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Key</label>
              <input
                type="text"
                value={type.key}
                readOnly
                className={`${input} font-mono bg-slate-50 text-slate-500`}
              />
              <p className="text-xs text-slate-400 mt-1">
                Immutable — every document stores this value.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create type'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DocumentTypesPage = () => {
  const { reload: reloadCatalog } = useDocumentTypes();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    try {
      setTypes(await documentTypesService.list({ includeInactive: true, withCounts: true }));
    } catch (err) {
      showToast('error', apiError(err, 'Failed to load document types'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const afterChange = async () => {
    await load();
    reloadCatalog(); // every other page reads the shared catalog
  };

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      if (editing === 'new') await documentTypesService.create(values);
      else await documentTypesService.update(editing.key, values);
      await afterChange();
      showToast('success', `Document type "${values.label}" saved`);
      setEditing(null);
    } catch (err) {
      showToast('error', apiError(err, 'Failed to save document type'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete document type "${t.label}"?`)) return;
    setBusyKey(t.key);
    try {
      await documentTypesService.remove(t.key);
      await afterChange();
      showToast('success', `"${t.label}" deleted`);
    } catch (err) {
      showToast('error', apiError(err, 'Failed to delete document type'));
    } finally {
      setBusyKey(null);
    }
  };

  const toggleActive = async (t) => {
    setBusyKey(t.key);
    try {
      await documentTypesService.update(t.key, { is_active: !t.is_active });
      await afterChange();
    } catch (err) {
      showToast('error', apiError(err, 'Failed to update document type'));
    } finally {
      setBusyKey(null);
    }
  };

  if (loading) {
    return <div className="page-container"><p className="text-slate-400 text-sm">Loading…</p></div>;
  }

  return (
    <div className="page-container max-w-5xl">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 mb-1">Document types</h2>
          <p className="text-slate-500">
            The classifications documents can carry. Adding one here makes it available
            everywhere — pickers, filters and sync sources — with no code change.
          </p>
        </div>
        <button
          onClick={() => setEditing('new')}
          className="shrink-0 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          + Add type
        </button>
      </div>

      {toast && (
        <div
          className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium ${
            toast.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'
          }`}
        >
          {toast.msg}
        </div>
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-xs text-slate-500">
              <th className="text-left font-medium py-2.5 pr-3">Label</th>
              <th className="text-left font-medium py-2.5 px-3">Key</th>
              <th className="text-right font-medium py-2.5 px-3">Documents</th>
              <th className="text-center font-medium py-2.5 px-3">Order</th>
              <th className="text-center font-medium py-2.5 px-3">Active</th>
              <th className="py-2.5 pl-3 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {types.map((t) => (
              <tr key={t.key} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60">
                <td className="py-3 pr-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-800">{t.label}</span>
                    {t.is_builtin && (
                      <span
                        title="Built-in type — can be deactivated but not deleted"
                        className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-slate-100 text-slate-500"
                      >
                        <HiLockClosed className="w-3 h-3" /> built-in
                      </span>
                    )}
                  </div>
                  {t.description && <p className="text-xs text-slate-400 mt-0.5">{t.description}</p>}
                </td>
                <td className="py-3 px-3 font-mono text-xs text-slate-500">{t.key}</td>
                <td className="py-3 px-3 text-right tabular-nums text-slate-600">
                  {(t.document_count ?? 0).toLocaleString()}
                </td>
                <td className="py-3 px-3 text-center tabular-nums text-slate-500">{t.sort_order}</td>
                <td className="py-3 px-3 text-center">
                  <input
                    type="checkbox"
                    checked={t.is_active}
                    disabled={busyKey === t.key}
                    onChange={() => toggleActive(t)}
                    className="w-4 h-4 accent-blue-600 cursor-pointer disabled:opacity-50"
                    aria-label={`${t.label} active`}
                  />
                </td>
                <td className="py-3 pl-3">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => setEditing(t)}
                      disabled={busyKey === t.key}
                      title="Edit"
                      className="p-2 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-30"
                    >
                      <HiPencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(t)}
                      disabled={busyKey === t.key || t.is_builtin || (t.document_count ?? 0) > 0}
                      title={
                        t.is_builtin
                          ? 'Built-in types cannot be deleted — deactivate instead'
                          : (t.document_count ?? 0) > 0
                          ? 'In use by documents — deactivate instead'
                          : 'Delete'
                      }
                      className="p-2 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-transparent"
                    >
                      <HiTrash className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-400 mt-3">
        Deactivating hides a type from new pickers; documents already classified with it keep
        their value and stay searchable. A type in use cannot be deleted.
      </p>

      {editing && (
        <FormModal
          type={editing === 'new' ? null : editing}
          saving={saving}
          onSubmit={handleSubmit}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  );
};

export default DocumentTypesPage;
